import { LoggerFactory } from "../../../logger";
import { reduce, map, clone, isFunction, forEach } from "lodash";
import { Node } from "./graph";

const log = LoggerFactory.create("completer");

export interface Completion {
  value: string;
  meta: string;
  type?: string;
  caption?: string;
  snippet?: any;
  score?: number;
  exactMatch?: number;
  docHTML?: string;
}

export interface CompleterInterface {
  getCompletions(
    editor: any,
    session: any,
    pos: any,
    prefix: string,
    callback: (...args: any) => void
  ): void;
}

export interface CompleterWrapper {
  completer: CompleterDefinition[];
  types: CompleterTypes;
}

export interface CompleterTypes {
  [key: string]: string[];
}

export interface CompleterDefinition {
  description?: string;
  startSymbol: string;
  endSymbol: string;
  type: string;
  list: CompleterNext[];
}

export interface CompleterNext {
  name: string;
  description?: string;
  next: CompleterNext[];
}

export class Completer implements CompleterInterface {
  public type: string;
  private completions: any;
  private startSymbol: string;
  private endSymbol: string;
  // @ts-ignore
  private description: string;

  static strToCompletion(key: string, node?: Node): Completion {
    const str = key;
    const out: Completion = { value: str, caption: str, meta: "" };

    if (node && isFunction(node.getData)) {
      out.docHTML = node.getData().description;
    }

    return out;
  }

  constructor(completions: Node) {
    this.completions = completions;
    this.startSymbol = completions.getData().startSymbol;
    this.endSymbol = completions.getData().endSymbol;
    this.description = completions.getData().description;
    this.type = completions.getData().type;
  }

  public next(seq: string[]) {
    const out: any[] = [];
    const completions = this.completions.xpath(seq);
    reduce(
      completions,
      (arr, node, key) => {
        const strOrNull = Completer.strToCompletion(key, node);

        if (strOrNull !== null) {
          arr.push(strOrNull);
        }

        return arr;
      },
      out
    );

    return out;
  }

  public getCompletions(
    editor: any,
    session: any,
    pos: any,
    prefix: string,
    callback: (...args: any) => void
  ): void {
    //log.debug('getCompletions()', arguments, this);

    /*log.debug('getCompletions() session:', session);
     log.debug('getCompletions() pos:', pos);
     log.debug('getCompletions() prefix:', prefix);*/

    var tokens = session.getTokens(pos.row),
      line = tokens.length ? map(tokens, "value").join("") : "",
      cursorColumn = editor.getCursorPosition().column,
      lineToCursor = line.substring(0, cursorColumn),
      lastBuckleOpen = lineToCursor.lastIndexOf(this.startSymbol),
      lastBuckleClosed = lineToCursor.lastIndexOf(this.endSymbol),
      isInsideBuckle = lastBuckleOpen > lastBuckleClosed,
      context;

    const completions: Completion[] = [];
    let seq: string[] = [];

    if (!isInsideBuckle) {
      isInsideBuckle = this.doubleCheckBracketsHardCode(
        lineToCursor,
        lastBuckleOpen
      );
    }

    if (!isInsideBuckle) {
      context = null;
    } else {
      context = this.type;
      seq = this.getSequence(
        cursorColumn,
        lineToCursor,
        lastBuckleOpen,
        lastBuckleClosed,
        isInsideBuckle
      );
      completions.push.apply(completions, this.next(clone(seq)));
    }

    if (!isInsideBuckle || (completions.length === 0 && seq.length <= 1)) {
      //HACK:CRIN-MGM: second check is hack that adds autocompletion and description for second carrier in
      // connect(carrier(carrier()))

      var mapped = {};
      mapped[this.startSymbol + this.endSymbol] = this.completions;
      completions.push.apply(
        completions,
        map(mapped, (node, key) => Completer.strToCompletion(key, node))
      );
    }

    callback(
      context,
      completions,
      isInsideBuckle ? cursorColumn - lastBuckleOpen : undefined
    );
  }

  public doubleCheckBracketsHardCode(
    lineToCursor: string,
    lastBuckleOpen: number
  ) {
    //HARDCODE!!

    var startSymbolHasBracket = this.startSymbol.indexOf("(") !== -1;

    if (!startSymbolHasBracket || lastBuckleOpen === -1) {
      return false;
    }

    var innerText = lineToCursor.substring(
      lastBuckleOpen + this.startSymbol.length,
      lineToCursor.length
    );
    var bracketStack = 1;

    for (var i = 0; i < innerText.length; i++) {
      var char = innerText[i];

      if (char === ")") {
        bracketStack--;
      }

      if (char === "(") {
        bracketStack++;
      }

      if (bracketStack === 0) {
        return false;
      }
    }

    return bracketStack > 0;
  }

  public getSequence(
    cursorColumn: number,
    lineToCursor: string,
    lastBuckleOpen: number,
    lastBuckleClosed: number,
    isInsideBuckle: boolean
  ): string[] {
    log.debug("getSequence()", arguments);

    log.debug("lineToCursor", lineToCursor);
    log.debug("lastBuckleOpen", lastBuckleOpen);
    log.debug("lastBuckleClosed", lastBuckleClosed);

    let out: string[];

    if (isInsideBuckle) {
      log.debug("inside buckle");
      var inBuckle = lineToCursor
        .substr(lastBuckleOpen + this.startSymbol.length, cursorColumn)
        .split(".");

      log.debug("inBuckle", inBuckle);

      out = inBuckle;
    } else {
      log.debug("outside buckle");

      out = [];
    }

    return out;
  }
}

export function fromJson(json: CompleterWrapper): Node[] {
  return map(json.completer, completer => {
    return completerNode(completer);
  });
}

function completerNode(json: CompleterDefinition): Node {
  const out = new Node();

  out.setData(json);

  if (!json.description) {
    log.debug("no description", json);
  }

  forEach(json.list, it => {
    let name = it.name;
    out.next[name] = new Node();

    jsonNode(it, out.next[name]);
  });

  return out;
}

function jsonNode(json: CompleterNext, node: Node) {
  const next = json.next || [];

  node.setData({ description: json.description });

  forEach(next, (it, key) => {
    let name = it.name || key;
    node.next[name] = new Node();

    jsonNode(it, node.next[name]);
  });
}
